import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { ru as dayPickerRu } from 'react-day-picker/locale';
import 'react-day-picker/dist/style.css';
import { Calendar as CalendarIcon, X } from 'lucide-react';
import { Popover } from './popover.js';
import { Button } from './button.js';
import { Calendar } from './calendar.js';
import { cn } from '../lib/cn.js';
/** Админка русская: подписи и календарь по умолчанию — по-русски. */
export const DATE_RANGE_PICKER_DEFAULT_LABELS = {
    clear: 'Сбросить',
    apply: 'Применить',
};
const DEFAULT_LOCALE = 'ru-RU';
const DateRangePickerContext = React.createContext(null);
function useCtx(name) {
    const ctx = React.useContext(DateRangePickerContext);
    if (!ctx)
        throw new Error(`<${name}> must be rendered inside <DateRangePicker>`);
    return ctx;
}
function fmt(d, locale) {
    return new Intl.DateTimeFormat(locale, {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
    }).format(d);
}
const Root = React.forwardRef(({ value, onChange, placeholder = 'Период', locale = DEFAULT_LOCALE, labels, children, className, numberOfMonths = 2, applyMode = 'instant', }, ref) => {
    const [open, setOpen] = React.useState(false);
    const clearLabel = labels?.clear ?? DATE_RANGE_PICKER_DEFAULT_LABELS.clear;
    const applyLabel = labels?.apply ?? DATE_RANGE_PICKER_DEFAULT_LABELS.apply;
    const ctx = React.useMemo(() => ({
        value,
        onChange,
        open,
        setOpen,
        placeholder,
        locale,
        labels: { clear: clearLabel, apply: applyLabel },
        applyMode,
    }), [value, onChange, open, placeholder, locale, clearLabel, applyLabel, applyMode]);
    return (_jsx(DateRangePickerContext.Provider, { value: ctx, children: _jsxs(Popover, { open: open, onOpenChange: setOpen, children: [_jsx("div", { ref: ref, className: cn('inline-flex', className), children: children ?? _jsx(DateRangePickerTrigger, {}) }), _jsx(Popover.Content, { className: "w-auto p-0", align: "start", sideOffset: 6, children: _jsx(DateRangePickerCalendar, { numberOfMonths: numberOfMonths }) })] }) }));
});
Root.displayName = 'DateRangePicker';
const DateRangePickerTrigger = React.forwardRef(({ placeholder, className }, ref) => {
    const { value, placeholder: ctxPlaceholder, locale } = useCtx('DateRangePicker.Trigger');
    const label = (() => {
        if (value.from && value.to)
            return `${fmt(value.from, locale)} – ${fmt(value.to, locale)}`;
        if (value.from)
            return `${fmt(value.from, locale)} – …`;
        return placeholder ?? ctxPlaceholder;
    })();
    const hasValue = Boolean(value.from || value.to);
    return (_jsx(Popover.Trigger, { asChild: true, children: _jsxs(Button, { ref: ref, type: "button", variant: "secondary", size: "sm", className: cn('gap-2 font-normal', !hasValue && 'text-[var(--color-text-muted)]', className), children: [_jsx(CalendarIcon, { className: "h-4 w-4 opacity-70", "aria-hidden": "true" }), _jsx("span", { className: "truncate", children: label })] }) }));
});
DateRangePickerTrigger.displayName = 'DateRangePicker.Trigger';
function DateRangePickerCalendar({ numberOfMonths }) {
    const { value, onChange, setOpen, locale, labels, applyMode } = useCtx('DateRangePicker.Calendar');
    const confirm = applyMode === 'confirm';
    // Черновик режима `confirm`: календарь монтируется при каждом открытии
    // поповера, поэтому черновик всегда начинается с применённого значения, а
    // закрытие без «Применить» его просто теряет.
    const [draft, setDraft] = React.useState(value);
    const shown = confirm ? draft : value;
    const selected = shown.from || shown.to ? { from: shown.from, to: shown.to } : undefined;
    function handleSelect(range, triggerDate) {
        if (!confirm) {
            onChange({ from: range?.from, to: range?.to });
            return;
        }
        if (!triggerDate)
            return;
        // Свой разбор кликов вместо `addToRange`: при готовом диапазоне клик
        // начинает новый (а не растягивает старый), второй клик его замыкает.
        setDraft((d) => d.from && !d.to
            ? triggerDate < d.from
                ? { from: triggerDate, to: d.from }
                : { from: d.from, to: triggerDate }
            : { from: triggerDate, to: undefined });
    }
    function clear() {
        // Применённого периода нет — сбрасывать нечего, кроме черновика.
        if (confirm && !value.from && !value.to) {
            setDraft({ from: undefined, to: undefined });
            return;
        }
        onChange({ from: undefined, to: undefined });
        if (confirm)
            setOpen(false);
    }
    function apply() {
        // Один выбранный день — однодневный период.
        if (confirm && draft.from)
            onChange({ from: draft.from, to: draft.to ?? draft.from });
        setOpen(false);
    }
    return (_jsxs("div", { className: "flex flex-col", children: [_jsx(Calendar, { mode: "range", numberOfMonths: numberOfMonths, selected: selected, onSelect: handleSelect, ...(value.from || value.to ? { defaultMonth: (value.from ?? value.to) } : {}), ...(locale.toLowerCase().startsWith('ru') ? { locale: dayPickerRu } : {}) }), _jsxs("div", { className: "flex items-center justify-between gap-2 border-t border-[var(--color-border-subtle)] p-2", children: [_jsxs(Button, { type: "button", variant: "ghost", size: "sm", onClick: clear, disabled: !value.from && !value.to && !(confirm && draft.from), children: [_jsx(X, { className: "mr-1 h-3.5 w-3.5", "aria-hidden": "true" }), labels.clear] }), _jsx(Button, { type: "button", variant: "primary", size: "sm", onClick: apply, disabled: confirm && !draft.from, children: labels.apply })] })] }));
}
export const DateRangePicker = Object.assign(Root, {
    Trigger: DateRangePickerTrigger,
});
//# sourceMappingURL=date-range-picker.js.map